import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { CommandManager } from '../../../core/commands/CommandManager';
import type { IFrame } from '../../../core/frames/IFrame';
import { loadCanvasPluginsForType } from '../../../core/canvas/CanvasPluginRegistry';
import type { CanvasPlugin } from '../../../core/canvas/CanvasPlugin';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';

type PreparedPluginRegistration = {
  plugin: CanvasPlugin;
  context: Parameters<NonNullable<CanvasPlugin['register']>>[0];
};

export async function loadAndPrepareCanvasPlugins(input: {
  requiredTypes: ReadonlySet<DiagramType>;
  toolRegistry: ToolRegistry;
  commandManager: CommandManager;
  loadPlugins?: typeof loadCanvasPluginsForType;
}): Promise<{
  plugins: CanvasPlugin[];
  deferredRegistrations: PreparedPluginRegistration[];
}> {
  const loadPlugins = input.loadPlugins ?? loadCanvasPluginsForType;
  const contextBase = {
    toolRegistry: input.toolRegistry,
    commandManager: input.commandManager,
  } as const;
  const loaded = new Map<string, PreparedPluginRegistration>();
  const deferred = new Map<string, PreparedPluginRegistration>();

  for (const type of input.requiredTypes) {
    const context = { ...contextBase, diagramType: type };
    const pluginsForType = await loadPlugins(type, contextBase);
    for (const plugin of pluginsForType) {
      if (loaded.has(plugin.id)) continue;
      const registration = { plugin, context };
      if (plugin.prepareHost) {
        await plugin.prepareHost(context);
        deferred.set(plugin.id, registration);
      } else {
        await plugin.register?.(context);
      }
      loaded.set(plugin.id, registration);
    }
  }

  return {
    plugins: Array.from(loaded.values(), ({ plugin }) => plugin),
    deferredRegistrations: Array.from(deferred.values()),
  };
}

export function useCanvasPluginsLoader(input: {
  /**
   * The RESOLVED diagram type. Pass `undefined` while the type is still
   * resolving — never a placeholder. Keying plugin registration off a
   * fallback type (historically `'genogram'`) registers the wrong domain's
   * canvas tools during the unresolved window and races the correct plugin,
   * leaving charts with a foreign tool set (e.g. an org chart whose property
   * panel collapses to Notes-only because `orgchart-data` never registered).
   */
  diagramType: DiagramType | undefined;
  frames: IFrame[];
  toolRegistry: ToolRegistry;
  commandManager: CommandManager;
  canvasLogger: { error: (message: string, error?: unknown) => void };
  setCanvasPlugins: Dispatch<SetStateAction<CanvasPlugin[]>>;
  onLoadError?: (error: unknown) => void;
}): void {
  const {
    diagramType,
    frames,
    toolRegistry,
    commandManager,
    canvasLogger,
    setCanvasPlugins,
    onLoadError,
  } = input;

  // Load/register canvas plugins for any diagram types currently in play.
  useEffect(() => {
    let cancelled = false;

    const requiredTypes = new Set<DiagramType>();
    if (diagramType) requiredTypes.add(diagramType);
    frames.forEach((frame) => {
      try {
        if (frame.type) requiredTypes.add(frame.type as DiagramType);
      } catch {}
    });

    // Nothing resolved yet: don't register any domain plugin. Registering off
    // a placeholder type here is what leaks foreign tools and loses the race.
    if (requiredTypes.size === 0) return;

    void (async () => {
      const prepared = await loadAndPrepareCanvasPlugins({
        requiredTypes,
        toolRegistry,
        commandManager,
      });

      if (cancelled) return;
      setCanvasPlugins(prepared.plugins);

      // Host state unblocks rendering; authoring tools can register after that
      // state update without keeping a read-only projection behind the whole
      // tool bundle. A frame boundary gives React an opportunity to commit the
      // host before the deferred imports begin.
      if (prepared.deferredRegistrations.length > 0) {
        window.requestAnimationFrame(() => {
          if (cancelled) return;
          void Promise.all(prepared.deferredRegistrations.map(({ plugin, context }) =>
            plugin.register?.(context),
          )).catch((error) => {
            canvasLogger.error('Failed to register deferred canvas plugin tools', error);
          });
        });
      }
    })().catch((error) => {
      canvasLogger.error('Failed to load canvas plugins', error);
      onLoadError?.(error);
    });

    return () => {
      cancelled = true;
    };
    // Preserve existing dependency list from the inline effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramType, frames, toolRegistry, commandManager]);
}
